import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { Alert, Autocomplete, Box, ButtonBase, Button, Chip, createFilterOptions, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { MaterialBoxPacking, MaterialProcurementOrder, MaterialRateItem } from '../pages/OfficeEstimationPage';
import type { OrderRow } from './MaterialReceivingDialogs';
import {
  applyKey, BORDER, BORDER_STRONG, Chevron, CountPill, DARK, FAINT, fmt, FONT_HEADING, GOLD, GOLD_D, GOLD_WASH, GREEN, Keypad, MUTED, PANEL, QtyInput, r3, RED, Tile, TileText, toNum, WARN, WARN_WASH,
} from './phoneUi';
import {
  defaultDimensionUnitFor, DIMENSION_LABELS, DIMENSION_UNIT_LABEL, DIMENSION_UNITS, emptyPieceRow, pieceRowTotal, pieceShapeForUnit, totalPieceQuantity,
  type DimensionUnit, type PieceRow,
} from '../office/pieceQuantity';
import {
  deliveryReceiptFields, findOpenPoLines, groupLabel, libraryPicks, normVendor, notOnOrderPicks, openMaterialsByGroup, openVendors, orderGroup, orderTitle, pickForOrder, pickName, recentlyReceivedPicks, sameVendorName, vendorSuggestions,
  type DeliveryLine, type DeliveryMeta, type MaterialPick,
} from '../office/materialReceiving';
import { cartingAgentsFor, planCartingAgents } from '../office/carting';

const TAB_KEY = 'rcvTab';

const today = () => new Date().toISOString().slice(0, 10);

type Step = 'home' | 'lines' | 'qty' | 'nopo' | 'done';
type Browse = 'order' | 'recent';
type NoPo = { pickKey: string; other: boolean; name: string; unit: string; vendor: string; qty: string; boxes: string; piecesPerBox: string };
type NoPoLine = NoPo & { id: string };
const blankNoPo = (): NoPo => ({ pickKey: '', other: false, name: '', unit: 'Nos', vendor: '', qty: '', boxes: '', piecesPerBox: '' });
const LIBRARY_RESULT_LIMIT = 8;
type VendorOption = { name: string; hint: string; group: 'Suggested' | 'Other vendors' | 'Matches transporter' | 'New vendor'; isNew?: boolean };
type BoxEntry = { boxes: string; piecesPerBox: string };
const filterVendors = createFilterOptions<VendorOption>();
const UNIT_CHOICES = ['Nos', 'Kgs', 'MT', 'Bags', 'Boxes', 'Sft', 'Rft', 'Cum'];

const packingFor = (item: MaterialRateItem | undefined, assemblyType = '', variety = ''): MaterialBoxPacking | null =>
  item?.boxPackings?.find((packing) =>
    String(packing.assemblyType || '').trim().toLowerCase() === String(assemblyType || '').trim().toLowerCase()
    && String(packing.variety || '').trim().toLowerCase() === String(variety || '').trim().toLowerCase()
  ) || null;

const boxesToQty = (boxes: string, piecesPerBox: string | number, packing: MaterialBoxPacking | null) =>
  toNum(boxes) * toNum(piecesPerBox) * toNum(packing?.areaPerPiece);
const fixed2 = (value: number) => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function loadBrowse(): Browse {
  try { return window.localStorage.getItem(TAB_KEY) === 'recent' ? 'recent' : 'order'; } catch { return 'order'; }
}

/**
 * Enters a quantity as a list of pieces (length, or length × width, or length × width × height,
 * depending on the unit) instead of one typed number — for granite, glass, cut lengths, and anything
 * else priced by size rather than counted or weighed. Reports its running total to the parent.
 */
function PieceRowsEditor({ unit, defaultDimensionUnit, onTotal }: { unit: string; defaultDimensionUnit: DimensionUnit; onTotal: (total: number) => void }) {
  const shape = pieceShapeForUnit(unit);
  const [dimensionUnit, setDimensionUnit] = useState<DimensionUnit>(defaultDimensionUnit);
  const [rows, setRows] = useState<PieceRow[]>([emptyPieceRow(shape)]);
  const total = totalPieceQuantity(rows, dimensionUnit, unit);
  const onTotalRef = useRef(onTotal);
  onTotalRef.current = onTotal;
  useEffect(() => { onTotalRef.current(total); }, [total]);
  if (!shape) return null;
  const setDim = (index: number, dimIndex: number, value: string) =>
    setRows((previous) => previous.map((row, at) => at === index ? { ...row, dims: row.dims.map((d, di) => di === dimIndex ? value : d) } : row));
  const setCount = (index: number, value: string) => setRows((previous) => previous.map((row, at) => at === index ? { ...row, count: value } : row));
  return <Stack gap={1}>
    <TextField select label="Measured in" value={dimensionUnit} onChange={(event) => setDimensionUnit(event.target.value as DimensionUnit)} size="small" sx={{ maxWidth: 190 }}>
      {DIMENSION_UNITS.map((option) => <MenuItem key={option} value={option}>{DIMENSION_UNIT_LABEL[option]}</MenuItem>)}
    </TextField>
    {rows.map((row, index) => <Stack key={index} direction="row" gap={0.6} alignItems="center">
      {DIMENSION_LABELS[shape].map((label, dimIndex) => <TextField key={label} label={label} value={row.dims[dimIndex] ?? ''} onChange={(event) => setDim(index, dimIndex, event.target.value)} inputProps={{ inputMode: 'decimal', 'aria-label': `Piece ${index + 1} ${label}` }} sx={{ width: 78 }} />)}
      <TextField label="Nos" value={row.count} onChange={(event) => setCount(index, event.target.value)} inputProps={{ inputMode: 'numeric', 'aria-label': `Piece ${index + 1} Nos` }} sx={{ width: 58 }} />
      <Typography sx={{ minWidth: 56, fontSize: 12.5, color: MUTED, textAlign: 'right' }}>{fmt(pieceRowTotal(row, dimensionUnit, unit))}</Typography>
      {rows.length > 1 && <ButtonBase aria-label={`Remove piece ${index + 1}`} onClick={() => setRows((previous) => previous.filter((_, at) => at !== index))} sx={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${BORDER_STRONG}`, color: MUTED, fontSize: 13 }}>✕</ButtonBase>}
    </Stack>)}
    <Button size="small" onClick={() => setRows((previous) => [...previous, emptyPieceRow(shape)])} sx={{ alignSelf: 'flex-start', color: GOLD_D, textTransform: 'none' }}>+ Add piece</Button>
    <Typography sx={{ fontWeight: 700 }}>Total: {fmt(total)} {unit}</Typography>
  </Stack>;
}

/** Over/under note shown while a quantity is being entered. */
function QtyNote({ order, qty }: { order: OrderRow; qty: number }) {
  if (!(qty > 0)) return null;
  if (qty > order.pending) return <Typography sx={{ fontSize: 12.5, color: WARN, fontWeight: 600 }}>+{fmt(r3(qty - order.pending))} {order.unit} more than ordered</Typography>;
  if (qty < order.pending) return <Typography sx={{ fontSize: 12.5, color: MUTED }}>{fmt(r3(order.pending - qty))} {order.unit} will stay pending</Typography>;
  return <Typography sx={{ fontSize: 12.5, color: GREEN }}>Completes this line</Typography>;
}

export default function ReceivingScreen({ orders, materialItems, uploadPhoto, onSavePo, onSaveDirect, cartingAgentsByVendor = {}, onSaveCartingAgents }: {
  orders: OrderRow[];
  materialItems: MaterialRateItem[];
  uploadPhoto: (file: File) => Promise<{ url: string; path: string }>;
  onSavePo: (lines: DeliveryLine[], meta: DeliveryMeta) => Promise<void>;
  onSaveDirect: (order: Omit<MaterialProcurementOrder, 'id'>) => Promise<void>;
  /** A vendor's regular transporters — fixed per vendor, unrelated to rate. */
  cartingAgentsByVendor?: Record<string, string[]>;
  /** Called when a newly-typed transporter should be remembered against the vendor for next time. */
  onSaveCartingAgents?: (vendorName: string, agents: string[]) => void;
}) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [step, setStep] = useState<Step>('home');
  const [browse, setBrowse] = useState<Browse>(loadBrowse);
  const [query, setQuery] = useState('');
  const [vendorKey, setVendorKey] = useState<string | null>(null);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [boxEntries, setBoxEntries] = useState<Record<string, BoxEntry>>({});
  const [active, setActive] = useState<string | null>(null);
  const [pad, setPad] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAllLibrary, setShowAllLibrary] = useState(false);
  const [noPo, setNoPo] = useState<NoPo>(blankNoPo);
  const [noPoLines, setNoPoLines] = useState<NoPoLine[]>([]);
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [cartingAgent, setCartingAgent] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [doneText, setDoneText] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!photo) { setPreview(''); return; }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const vendors = useMemo(() => openVendors(orders), [orders]);
  const materialGroups = useMemo(() => openMaterialsByGroup(orders, materialItems), [orders, materialItems]);
  const picks = useMemo(() => libraryPicks(materialItems), [materialItems]);
  const recentPicks = useMemo(() => recentlyReceivedPicks(orders, picks, 5), [orders, picks]);
  const vendorLines = useMemo(() => vendors.find((vendor) => vendor.key === vendorKey)?.lines || [], [vendors, vendorKey]);
  const activeOrder = orders.find((order) => order.id === active) || null;
  const chosen = vendorLines.filter((order) => toNum(qty[order.id]) > 0);
  const chooseBrowse = (value: Browse | null) => {
    if (!value) return;
    setBrowse(value);
    try { window.localStorage.setItem(TAB_KEY, value); } catch { /* remembering the tab is optional */ }
  };
  // Starts a delivery without a PO, optionally with the material already chosen (or an "Other item" name).
  const startNoPo = (pick?: MaterialPick | null, otherName?: string) => {
    setNoPo(pick ? { ...blankNoPo(), pickKey: pick.key, unit: pick.unit } : otherName !== undefined ? { ...blankNoPo(), other: true, name: otherName } : blankNoPo());
    setNoPoLines([]); setVendorKey(null); setError(''); setStep('nopo');
  };
  const reset = () => {
    setStep('home'); setQuery(''); setVendorKey(null); setQty({}); setBoxEntries({}); setActive(null); setPad(''); setExpanded(null); setNoPo(blankNoPo()); setNoPoLines([]);
    setReference(''); setDate(today()); setNotes(''); setCartingAgent(''); setPhoto(null); setError(''); setDoneText('');
  };
  const back = () => {
    setError('');
    if (step === 'qty') setStep('lines');
    else { setStep('home'); setVendorKey(null); }
  };
  const openLine = (order: OrderRow) => {
    setVendorKey(normVendor(order.vendor)); setQty({}); setActive(order.id); setPad(''); setStep('qty');
  };
  const pressKey = (key: string) => setPad((previous) => applyKey(previous, key));
  const setQuantity = () => {
    if (active) setQty((previous) => { const next = { ...previous }; const value = toNum(pad); if (value > 0) next[active] = String(value); else delete next[active]; return next; });
    setStep('lines');
  };

  // ── search ──────────────────────────────────────────────────────────────────
  const needle = query.trim().toLowerCase();
  const libraryMatches = useMemo(() => notOnOrderPicks(picks, orders, query), [picks, orders, query]);
  const matchedVendors = vendors.filter((vendor) => !needle || vendor.label.toLowerCase().includes(needle));
  const matchedMaterials = needle
    ? materialGroups.flatMap((group) => group.materials).filter((material) => `${material.label} ${material.search}`.toLowerCase().includes(needle))
    : [];

  const vendorTiles = (list: typeof vendors) => list.map((vendor) => <Tile key={vendor.key || '__none'} onClick={() => { setVendorKey(vendor.key); setQty({}); setStep('lines'); }}>
    <TileText title={vendor.label} sub={`${vendor.lines.length} line${vendor.lines.length === 1 ? '' : 's'} expected`} /><CountPill n={vendor.lines.length} />
  </Tile>);

  // A material's open lines are shown as a plain list under its group's header bar, not as individual cards
  // — matching the vendor-grouped look on the Orders home screen.
  const materialRow = (material: (typeof materialGroups)[number]['materials'][number]) => {
    const single = material.lines.length === 1;
    const open = expanded === material.key;
    return <React.Fragment key={material.key}>
      <ButtonBase onClick={() => single ? openLine(material.lines[0]) : setExpanded(open ? null : material.key)} sx={{ width: '100%', display: 'flex', alignItems: 'center', textAlign: 'left', px: 1.5, py: 1, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
        <TileText title={material.label} sub={`${material.lines.length} vendor${single ? '' : 's'} · ${fmt(r3(material.pending))} ${material.unit} pending`} />
        {!single && <Typography sx={{ color: GOLD, fontSize: 20 }}>{open ? '▾' : '▸'}</Typography>}
      </ButtonBase>
      {open && <>
        {material.lines.map((order) => <ButtonBase key={order.id} onClick={() => openLine(order)} sx={{ width: '100%', display: 'flex', alignItems: 'center', textAlign: 'left', pl: 3, pr: 1.5, py: 0.9, borderTop: `1px solid ${BORDER}`, bgcolor: PANEL }}>
          <TileText size={14} title={order.vendor?.trim() || 'Vendor not set'} sub={`${order.poNumber ? `${order.poNumber} · ` : ''}${fmt(order.pending)} ${order.unit} pending`} />
        </ButtonBase>)}
        {pickForOrder(picks, material.lines[0]) && <ButtonBase onClick={() => startNoPo(pickForOrder(picks, material.lines[0]))} sx={{ width: '100%', display: 'flex', alignItems: 'center', textAlign: 'left', pl: 3, pr: 1.5, py: 0.9, borderTop: `1px dashed ${BORDER_STRONG}`, bgcolor: PANEL, color: GOLD_D }}>
          <TileText size={14} title="Different vendor / no PO" sub="Record it without an order" />
        </ButtonBase>}
      </>}
    </React.Fragment>;
  };

  // ── save ────────────────────────────────────────────────────────────────────
  // One number field: if a bill came instead of a challan, staff enter the bill number in it.
  const documentLabel = 'Challan number';
  const noPoPick: MaterialPick | null = picks.find((entry) => entry.key === noPo.pickKey) || null;
  const noPoPacking = packingFor(noPoPick?.item, noPoPick?.assemblyType, noPoPick?.variety);
  const noPoPiecesPerBox = noPo.piecesPerBox || String(noPoPacking?.piecesPerBox || '');
  const noPoQuantity = noPoPacking && toNum(noPo.boxes) > 0
    ? boxesToQty(noPo.boxes, noPoPiecesPerBox, noPoPacking)
    : toNum(noPo.qty);
  const vendorInfo = useMemo(() => vendorSuggestions(materialItems, orders, noPoPick), [materialItems, orders, noPoPick]);
  // Fast-tap vendors for this material only — quoted for it, or supplied it before. Never pads out with
  // an unrelated vendor just to fill the row.
  const vendorQuickPicks = useMemo(() => {
    const unique: string[] = [];
    vendorInfo.related.forEach(({ name }) => { if (!unique.some((existing) => sameVendorName(existing, name))) unique.push(name); });
    return unique.slice(0, 3);
  }, [vendorInfo]);
  const vendorChoices: VendorOption[] = [
    // Suggested (this material's own vendors) first, every other known vendor after — both always
    // available in the dropdown, just ordered so the relevant ones don't need scrolling past.
    ...vendorInfo.related.map((entry) => ({ ...entry, group: 'Suggested' as const })),
    ...vendorInfo.others.map((entry) => ({ ...entry, group: 'Other vendors' as const })),
  ];
  const noPoDuplicates = noPoPick ? findOpenPoLines(orders, { materialId: noPoPick.item.id, quoteOptionId: noPoPick.optionId, materialLabel: noPoPick.label }) : [];
  const vendorName = vendorKey !== null ? vendors.find((vendor) => vendor.key === vendorKey)?.label || '' : noPo.vendor.trim() || noPoLines[0]?.vendor.trim() || '';
  const cartingChoices = cartingAgentsFor(cartingAgentsByVendor, vendorName);
  const noPoPickFor = (line: NoPo): MaterialPick | null => picks.find((entry) => entry.key === line.pickKey) || null;
  const noPoPackingFor = (line: NoPo) => {
    const pick = noPoPickFor(line);
    return packingFor(pick?.item, pick?.assemblyType, pick?.variety);
  };
  const noPoPiecesPerBoxFor = (line: NoPo) => line.piecesPerBox || String(noPoPackingFor(line)?.piecesPerBox || '');
  const noPoQuantityFor = (line: NoPo) => {
    const packing = noPoPackingFor(line);
    return packing && toNum(line.boxes) > 0 ? boxesToQty(line.boxes, noPoPiecesPerBoxFor(line), packing) : toNum(line.qty);
  };
  const noPoLineStarted = (line: NoPo) => !!(line.pickKey || line.other || line.name.trim() || line.qty || line.boxes);
  const noPoLineTitle = (line: NoPo) => {
    if (line.other) return line.name.trim() || 'Other item';
    const pick = noPoPickFor(line);
    return pick ? pickName(pick).title : 'Material';
  };
  const noPoLineUnit = (line: NoPo) => line.other ? line.unit : noPoPickFor(line)?.unit || line.unit || 'Nos';
  const noPoLineProblem = (line: NoPo): string => {
    const pick = noPoPickFor(line);
    const name = line.other ? line.name.trim() : pick?.label;
    if (!name) return 'Choose the material, or enter the item name.';
    if (!line.vendor.trim()) return 'Enter the vendor.';
    if (noPoQuantityFor(line) <= 0) return noPoPackingFor(line) ? 'Enter boxes received.' : 'Enter the quantity received.';
    return '';
  };
  const receiptRefKey = (value?: string) => String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  const materialDuplicateKey = (value: Pick<MaterialProcurementOrder, 'materialId' | 'quoteOptionId' | 'materialAssemblyType' | 'materialVariety' | 'materialLabel'>) =>
    [
      value.materialId || '',
      value.quoteOptionId || '',
      value.materialAssemblyType || '',
      value.materialVariety || '',
      String(value.materialLabel || '').trim().toLowerCase().replace(/\s+/g, ' '),
    ].join('|');
  const noPoDuplicateKey = (line: NoPo) => {
    const pick = noPoPickFor(line);
    return materialDuplicateKey({
      materialId: pick?.item.id || '',
      quoteOptionId: pick?.optionId || '',
      materialAssemblyType: pick?.assemblyType || '',
      materialVariety: pick?.variety || '',
      materialLabel: line.other ? line.name.trim() : pick?.label || '',
    });
  };
  const noPoLinesForSubmit = () => [
    ...noPoLines,
    ...(noPoLineStarted(noPo) ? [{ ...noPo, id: '__current' }] : []),
  ];
  const addNoPoLine = () => {
    const problem = noPoLineProblem(noPo);
    if (problem) { setError(problem); return; }
    setNoPoLines((previous) => [...previous, { ...noPo, id: `nopo_${Date.now()}_${previous.length}` }]);
    setNoPo({ ...blankNoPo(), vendor: noPo.vendor.trim() });
    setError('');
  };
  const duplicateReceiptProblem = (): string => {
    const ref = receiptRefKey(reference);
    if (!ref) return '';
    const findDuplicate = (vendor: string, materialKey: string) => orders.find((order) =>
      sameVendorName(order.vendor || '', vendor)
      && materialDuplicateKey(order) === materialKey
      && (order.receipts || []).some((receipt) => receiptRefKey(receipt.reference || receipt.billNumber) === ref)
    );
    if (vendorKey !== null) {
      for (const line of chosen) {
        const duplicate = findDuplicate(line.vendor || '', materialDuplicateKey(line));
        if (duplicate) return `This challan is already received for ${orderTitle(line)} from ${line.vendor?.trim() || 'this vendor'}.`;
      }
      return '';
    }
    for (const line of noPoLinesForSubmit()) {
      const duplicate = findDuplicate(line.vendor, noPoDuplicateKey(line));
      if (duplicate) return `This challan is already received for ${noPoLineTitle(line)} from ${line.vendor.trim()}.`;
    }
    return '';
  };
  const duplicateReceiptWarning = duplicateReceiptProblem();

  /** What's missing before this delivery can be saved, whichever path it's on; empty once ready. */
  const validationProblem = (): string => {
    if (vendorKey !== null) {
      if (!chosen.length) return 'Enter a quantity for at least one line.';
    } else {
      const directLines = noPoLinesForSubmit();
      if (!directLines.length) return 'Choose the material, or enter the item name.';
      const currentProblem = noPoLineStarted(noPo) ? noPoLineProblem(noPo) : '';
      if (currentProblem) return currentProblem;
      const savedProblem = noPoLines.map(noPoLineProblem).find(Boolean);
      if (savedProblem) return savedProblem;
    }
    if (!reference.trim()) return `Enter the ${documentLabel.toLowerCase()}.`;
    const duplicate = duplicateReceiptProblem();
    if (duplicate) return duplicate;
    return '';
  };

  const submit = async () => {
    const problem = validationProblem();
    if (problem) { setError(problem); return; }
    setSaving(true); setError('');
    try {
      let uploaded: { url: string; path: string } | undefined;
      if (photo) {
        try { uploaded = await uploadPhoto(photo); }
        catch { setError('The photo could not be uploaded. Try again, or remove the photo to save without it.'); return; }
      }
      const meta: DeliveryMeta = { date, reference: reference.trim(), notes: notes.trim(), documentType: 'challan', photo: uploaded, cartingAgent: cartingAgent.trim() };
      const submittingVendorName = vendorKey !== null ? vendors.find((vendor) => vendor.key === vendorKey)?.label || '' : noPo.vendor.trim() || noPoLines[0]?.vendor.trim() || '';
      if (onSaveCartingAgents && submittingVendorName && meta.cartingAgent) {
        const next = planCartingAgents(cartingAgentsFor(cartingAgentsByVendor, submittingVendorName), meta.cartingAgent);
        if (next.length !== cartingAgentsFor(cartingAgentsByVendor, submittingVendorName).length) onSaveCartingAgents(submittingVendorName, next);
      }
      if (vendorKey !== null) {
        await onSavePo(chosen.map((order) => {
          const pick = pickForOrder(picks, order);
          const packing = packingFor(pick?.item, pick?.assemblyType, pick?.variety);
          const entry = boxEntries[order.id];
          return {
            orderId: order.id,
            qty: toNum(qty[order.id]),
            ...(packing && entry && toNum(entry.boxes) > 0 ? { boxes: toNum(entry.boxes), piecesPerBox: toNum(entry.piecesPerBox || packing.piecesPerBox), areaPerPiece: packing.areaPerPiece } : {}),
          };
        }), meta);
        setDoneText('Accounts will match the bill later.');
      } else {
        const directLines = noPoLinesForSubmit();
        await Promise.all(directLines.map((line, index) => {
          const pick = noPoPickFor(line);
          const packing = noPoPackingFor(line);
          const quantity = noPoQuantityFor(line);
          const info = vendorSuggestions(materialItems, orders, pick);
          const known = [...info.related, ...info.others].find((entry) => sameVendorName(entry.name, line.vendor));
          const lineVendorName = known?.name || line.vendor.trim();
          const quote = pick ? (pick.item.quotes || []).find((candidate) => (candidate.status || 'active') !== 'voided' && sameVendorName(candidate.vendor, lineVendorName)) : undefined;
          const option = (quote?.quoteOptions || []).find((candidate) => candidate.id === pick?.optionId);
          return onSaveDirect({
            materialId: pick?.item.id || '',
            quoteId: quote?.id || '',
            quoteOptionId: option?.id || pick?.optionId || '',
            materialAssemblyType: pick?.assemblyType || '',
            materialVariety: pick?.variety || '',
            materialLabel: line.other ? line.name.trim() : pick?.label || '',
            unit: line.other ? line.unit : pick?.unit || 'Nos',
            vendor: lineVendorName,
            newVendor: !known,
            brand: option?.brand || quote?.brand || pick?.item.brand || '',
            code: option?.code || quote?.materialCode || '',
            orderDate: meta.date,
            orderQty: quantity,
            poNumber: '',
            notes: meta.notes,
            scopeType: 'project',
            needsMapping: line.other,
            receipts: [{ id: `receipt_${Date.now()}_${index}`, date: meta.date, qty: quantity, ...(packing && toNum(line.boxes) > 0 ? { boxes: toNum(line.boxes), piecesPerBox: toNum(noPoPiecesPerBoxFor(line)), areaPerPiece: packing.areaPerPiece } : {}), ...deliveryReceiptFields(meta) }],
          });
        }));
        setDoneText(`${directLines.length} material${directLines.length === 1 ? '' : 's'} saved. Purchasing will confirm this delivery.`);
      }
      setStep('done');
    } catch (err: any) {
      setError(err?.message || 'Could not save the receipt.');
    } finally { setSaving(false); }
  };

  // ── screens ─────────────────────────────────────────────────────────────────
  const Bar = ({ title, sub, canBack }: { title: string; sub?: string; canBack?: boolean }) => <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.75, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${BORDER}` }}>
    {canBack && <ButtonBase onClick={back} aria-label="Back" sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: PANEL, border: `1px solid ${BORDER}`, fontSize: 20 }}>←</ButtonBase>}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 19, color: DARK }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: 12.5, color: MUTED }}>{sub}</Typography>}
    </Box>
  </Stack>;
  const Footer = ({ children }: { children: React.ReactNode }) => <Stack direction="row" alignItems="center" gap={1.25} sx={{ px: 1.75, py: 1.5, borderTop: `1px solid ${BORDER}`, bgcolor: PANEL }}>{children}</Stack>;
  const Primary = (props: React.ComponentProps<typeof Button>) => <Button variant="contained" {...props} sx={{ minHeight: 48, borderRadius: '15px', px: 2.5, fontWeight: 600, bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D }, ...(props.sx as object) }} />;

  // Challan/bill number, carting agent, photo and notes — shared by both the "receive against PO" and
  // "No PO delivery" screens, right below the material/vendor/quantity fields on the same page.
  const deliveryDetailsFields = () => <>
    <TextField label={documentLabel} value={reference} onChange={(event) => { setReference(event.target.value); setError(''); }} inputProps={{ autoCapitalize: 'characters' }} fullWidth />
    {vendorName && <Autocomplete
      freeSolo
      options={cartingChoices}
      inputValue={cartingAgent}
      onInputChange={(_, value) => setCartingAgent(value)}
      onChange={(_, value) => setCartingAgent(value || '')}
      renderInput={(params) => <TextField {...params} label="Carting agent (optional)" placeholder="Who transported this load" helperText={cartingChoices.length ? `${vendorName}'s saved transporters are listed first.` : `No transporters saved for ${vendorName} yet.`} fullWidth />}
    />}
    <TextField label="Date" type="date" value={date} onChange={(event) => setDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
    <input ref={fileInput} type="file" accept="image/*" capture="environment" hidden aria-label="Choose photo" onChange={(event) => { const file = event.target.files?.[0]; if (file) { setPhoto(file); setError(''); } event.target.value = ''; }} />
    {photo ? <Stack direction="row" gap={1.25} alignItems="center" sx={{ p: 1, border: `1px solid ${BORDER}`, borderRadius: '15px' }}>
      {preview && <Box component="img" src={preview} alt="Challan or bill photo" sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: '15px' }} />}
      <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>Photo added</Typography>
      <Button size="small" onClick={() => setPhoto(null)} sx={{ color: RED }}>Remove</Button>
    </Stack> : <Tile onClick={() => fileInput.current?.click()}><Box sx={{ flex: 1, textAlign: 'center' }}><Typography sx={{ fontWeight: 500 }}>Add photo</Typography></Box></Tile>}
    <TextField label="Notes (optional)" value={notes} onChange={(event) => setNotes(event.target.value)} fullWidth />
  </>;

  let content: React.ReactNode;
  if (step === 'home') {
    content = <>

      <Stack gap={1.25} sx={{ flex: 1, p: 1.75 }}>
        <TextField value={query} onChange={(event) => { setQuery(event.target.value); setShowAllLibrary(false); }} placeholder="Search vendor or material" InputProps={{ sx: { borderRadius: '15px', fontSize: 16, bgcolor: PANEL } }} inputProps={{ 'aria-label': 'Search vendor or material' }} fullWidth />
        {!needle && <ToggleButtonGroup exclusive fullWidth value={browse} onChange={(_, value) => chooseBrowse(value)} aria-label="Browse by" sx={{ '& .MuiToggleButton-root': { minHeight: 46, textTransform: 'none', fontWeight: 600, borderRadius: '15px', '&.Mui-selected': { bgcolor: GOLD_WASH, color: GOLD_D, border: `2px solid ${GOLD}` } } }}>
          <ToggleButton value="order">Order</ToggleButton><ToggleButton value="recent">Recent material used</ToggleButton>
        </ToggleButtonGroup>}
        {needle ? <>
          {matchedVendors.length > 0 && <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: FAINT }}>Vendors</Typography>}
          {vendorTiles(matchedVendors)}
          {matchedMaterials.length > 0 && <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: FAINT }}>Materials</Typography>}
          {matchedMaterials.map(materialRow)}
          {libraryMatches.length > 0 && <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: FAINT }}>Not on order</Typography>}
          {(showAllLibrary ? libraryMatches : libraryMatches.slice(0, LIBRARY_RESULT_LIMIT)).map((pick) => <Tile key={pick.key} onClick={() => startNoPo(pick)}>
            <TileText title={[pick.assemblyType, pick.variety].filter(Boolean).join(' · ') || pick.item.materialName} sub={`${groupLabel(String(pick.item.group))} · No open PO · record as a no-PO delivery`} /><Chevron />
          </Tile>)}
          {!showAllLibrary && libraryMatches.length > LIBRARY_RESULT_LIMIT && <Button onClick={() => setShowAllLibrary(true)} sx={{ minHeight: 44, color: GOLD_D, fontWeight: 600 }}>Show all {libraryMatches.length}</Button>}
          {!matchedVendors.length && !matchedMaterials.length && !libraryMatches.length && <>
            <Typography sx={{ color: MUTED, fontSize: 13 }}>Nothing in the library matches “{query.trim()}”.</Typography>
            <Tile dashed onClick={() => startNoPo(null, query.trim())}><TileText title={`Add “${query.trim()}” as an Other item`} sub="Purchasing will map it to a material later" /><Chevron /></Tile>
          </>}
        </> : browse === 'order' ? (materialGroups.length ? <Stack gap={1.25}>{materialGroups.map((group) => <Box key={group.group} sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden' }}>
            <ButtonBase onClick={() => setCollapsed((previous) => ({ ...previous, [group.group]: !previous[group.group] }))} aria-expanded={!collapsed[group.group]} sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', px: 1.5, py: 0.9, width: '100%', bgcolor: PANEL }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: FAINT }}>{group.group}</Typography>
              <Box sx={{ ml: 'auto', bgcolor: GOLD_WASH, color: GOLD_D, borderRadius: '15px', px: 1, fontSize: 12, fontWeight: 600 }}>{group.materials.length}</Box>
              <Typography sx={{ color: FAINT, fontSize: 13 }}>{collapsed[group.group] ? '▸' : '▾'}</Typography>
            </ButtonBase>
            {!collapsed[group.group] && group.materials.map(materialRow)}
          </Box>)}</Stack> : <Typography sx={{ color: MUTED, fontSize: 13 }}>No open purchase orders.</Typography>)
          : (recentPicks.length ? recentPicks.map((pick) => <Tile key={pick.key} onClick={() => startNoPo(pick)}>
            <TileText title={[pick.assemblyType, pick.variety].filter(Boolean).join(' · ') || pick.item.materialName} sub={`${groupLabel(String(pick.item.group))} · Tap to record a no-PO delivery`} /><Chevron />
          </Tile>) : <Typography sx={{ color: MUTED, fontSize: 13 }}>Nothing received yet.</Typography>)}
        <Tile dashed onClick={() => startNoPo()}>
          <TileText title="No PO delivery" sub="It was not ordered yet" /><Chevron />
        </Tile>
      </Stack>
    </>;
  } else if (step === 'lines') {
    content = <>
      <Bar title={vendors.find((vendor) => vendor.key === vendorKey)?.label || 'Delivery'} canBack />
      <Stack gap={1.25} sx={{ flex: 1, p: 1.75 }}>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Tap a line and enter what arrived. Leave the rest.</Typography>
        {vendorLines.map((order) => {
          const value = toNum(qty[order.id]);
          return <Tile key={order.id} selected={value > 0} onClick={() => { setActive(order.id); setPad(qty[order.id] || ''); setStep('qty'); }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 500, fontSize: 15, color: DARK }}>{orderTitle(order)}</Typography>
              {orderGroup(order) && <Typography sx={{ fontSize: 12.5, color: MUTED }}>{orderGroup(order)}</Typography>}
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>{order.poNumber ? `${order.poNumber} · ` : ''}ordered {fmt(order.orderQty)} · pending {fmt(order.pending)} {order.unit}</Typography>
              {toNum(order.orderedRateInclGst) > 0 && <Typography sx={{ fontSize: 12, color: GOLD_D, fontWeight: 700 }}>Ordered rate ₹{fixed2(toNum(order.orderedRateInclGst))}/{order.unit} incl GST</Typography>}
              <QtyNote order={order} qty={value} />
            </Box>
            <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>{value > 0 ? `${fmt(value)} ${order.unit}` : <span style={{ color: FAINT, fontWeight: 400 }}>Add</span>}</Typography>
          </Tile>;
        })}
        <Typography sx={{ color: MUTED, fontSize: 13 }}>{chosen.length ? `${chosen.length} line${chosen.length === 1 ? '' : 's'} to receive` : 'Nothing selected yet'}</Typography>
        {deliveryDetailsFields()}
        {duplicateReceiptWarning && <Alert severity="warning" sx={{ borderRadius: '15px' }}>{duplicateReceiptWarning}</Alert>}
        {error && <Typography sx={{ color: RED, fontSize: 13 }}>{error}</Typography>}
      </Stack>
      <Footer><Primary fullWidth disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save receipt'}</Primary></Footer>
    </>;
  } else if (step === 'qty' && activeOrder) {
    const value = toNum(pad);
    const pieceItem = materialItems.find((candidate) => candidate.id === activeOrder.materialId);
    const orderPick = pickForOrder(picks, activeOrder);
    const orderPacking = packingFor(orderPick?.item, orderPick?.assemblyType, orderPick?.variety);
    const boxEntry = boxEntries[activeOrder.id] || { boxes: '', piecesPerBox: String(orderPacking?.piecesPerBox || '') };
    const byPieces = !!pieceItem?.pieceEntry && pieceShapeForUnit(activeOrder.unit) > 0;
    content = <>
      <Bar title={orderTitle(activeOrder)} sub={orderGroup(activeOrder)} canBack />
      <Stack gap={1.5} sx={{ flex: 1, p: 1.75 }}>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>{activeOrder.vendor?.trim() || 'Vendor not set'} · {activeOrder.poNumber ? `${activeOrder.poNumber} · ` : ''}ordered {fmt(activeOrder.orderQty)} · pending {fmt(activeOrder.pending)} {activeOrder.unit}</Typography>
        {toNum(activeOrder.orderedRateInclGst) > 0 && <Typography sx={{ color: GOLD_D, fontSize: 12.5, fontWeight: 700 }}>Ordered rate ₹{fixed2(toNum(activeOrder.orderedRateInclGst))}/{activeOrder.unit} incl GST</Typography>}
        {orderPacking
          ? <Stack gap={1} sx={{ p: 1.25, borderRadius: '15px', bgcolor: GOLD_WASH }}>
            <Typography sx={{ fontWeight: 700, color: GOLD_D, fontSize: 13 }}>Receive in boxes</Typography>
            <Stack direction="row" gap={0.75}>
              <TextField label="Boxes received" value={boxEntry.boxes} onChange={(event) => {
                const next = { ...boxEntry, boxes: event.target.value };
                setBoxEntries((previous) => ({ ...previous, [activeOrder.id]: next }));
                setPad(String(boxesToQty(next.boxes, next.piecesPerBox, orderPacking)));
              }} inputProps={{ inputMode: 'decimal' }} fullWidth />
              <TextField label="Tiles / box" value={boxEntry.piecesPerBox} onChange={(event) => {
                const next = { ...boxEntry, piecesPerBox: event.target.value };
                setBoxEntries((previous) => ({ ...previous, [activeOrder.id]: next }));
                setPad(String(boxesToQty(next.boxes, next.piecesPerBox, orderPacking)));
              }} inputProps={{ inputMode: 'numeric' }} fullWidth />
            </Stack>
            <Typography sx={{ fontSize: 13, color: DARK }}>Calculated: <b>{fmt(value)} {activeOrder.unit}</b> ({fixed2(orderPacking.areaPerPiece)} {activeOrder.unit} per tile)</Typography>
            <QtyNote order={activeOrder} qty={value} />
          </Stack>
          : byPieces
          ? <>
            <PieceRowsEditor key={activeOrder.id} unit={activeOrder.unit} defaultDimensionUnit={pieceItem?.pieceDimensionUnit || defaultDimensionUnitFor(activeOrder.unit)} onTotal={(total) => setPad(total > 0 ? String(total) : '')} />
            <QtyNote order={activeOrder} qty={value} />
          </>
          : <>
            <Box sx={{ textAlign: 'center', py: 0.5 }}>
              <QtyInput value={pad} unit={activeOrder.unit} onChange={setPad} />
              <QtyNote order={activeOrder} qty={value} />
            </Box>
            <Keypad onKey={pressKey} />
          </>}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <Button variant="outlined" onClick={() => setPad(String(activeOrder.pending))} sx={{ minHeight: 48, borderRadius: '15px', color: DARK, borderColor: BORDER_STRONG, fontWeight: 600 }}>Full ({fmt(activeOrder.pending)})</Button>
          <Primary onClick={setQuantity}>Set</Primary>
        </Box>
        {pickForOrder(picks, activeOrder) && <Button onClick={() => startNoPo(pickForOrder(picks, activeOrder))} sx={{ color: GOLD_D, fontWeight: 600 }}>Different vendor or no PO?</Button>}
      </Stack>
    </>;
  } else if (step === 'nopo') {
    content = <>
      <Bar title="No PO delivery" canBack />
      <Stack gap={1.5} sx={{ flex: 1, p: 1.75 }}>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Pick the material that arrived. Purchasing will confirm it.</Typography>
        {noPoLines.length > 0 && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden', bgcolor: '#fff' }}>
          <Stack direction="row" alignItems="center" sx={{ px: 1.25, py: 0.9, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: FAINT }}>Lines on this challan</Typography>
            <CountPill n={noPoLines.length} />
          </Stack>
          {noPoLines.map((line) => <Stack key={line.id} direction="row" alignItems="center" gap={1} sx={{ px: 1.25, py: 1, borderTop: `1px solid ${BORDER}` }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: DARK }}>{noPoLineTitle(line)}</Typography>
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>{line.vendor.trim()} · {fmt(noPoQuantityFor(line))} {noPoLineUnit(line)}</Typography>
            </Box>
            <Button size="small" onClick={() => setNoPoLines((previous) => previous.filter((entry) => entry.id !== line.id))} sx={{ minWidth: 0, color: RED, textTransform: 'none' }}>Remove</Button>
          </Stack>)}
        </Box>}
        <TextField select label="Material" value={noPo.other ? '__other' : noPo.pickKey} onChange={(event) => { setError(''); const value = event.target.value; if (value === '__other') setNoPo({ ...noPo, other: true, pickKey: '', qty: '', boxes: '', piecesPerBox: '' }); else { const pick = picks.find((entry) => entry.key === value); setNoPo({ ...noPo, other: false, pickKey: value, unit: pick?.unit || noPo.unit, qty: '', boxes: '', piecesPerBox: '' }); } }} fullWidth>
          {picks.map((entry) => <MenuItem key={entry.key} value={entry.key}><Box><Typography sx={{ fontSize: 15 }}>{pickName(entry).title}</Typography><Typography sx={{ fontSize: 12, color: MUTED }}>{pickName(entry).group}</Typography></Box></MenuItem>)}
          <MenuItem value="__other">Other item (not in the library)</MenuItem>
        </TextField>
        {noPo.other && <>
          <TextField label="Item name" value={noPo.name} onChange={(event) => setNoPo({ ...noPo, name: event.target.value })} fullWidth />
          <TextField select label="Unit" value={noPo.unit} onChange={(event) => setNoPo({ ...noPo, unit: event.target.value })} fullWidth>{UNIT_CHOICES.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}</TextField>
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>Flagged “needs mapping” until purchasing links it to a library material.</Typography>
        </>}
        {!noPo.other && noPoDuplicates.length > 0 && <Box sx={{ borderRadius: '15px', p: 1.5, bgcolor: WARN_WASH, border: `1px solid ${WARN}` }}>
          <Typography sx={{ fontWeight: 700, color: WARN, fontSize: 14 }}>There is an open PO for this material</Typography>
          <Typography sx={{ fontSize: 13, color: DARK, mt: 0.5 }}>{noPoDuplicates.map((order) => `${order.vendor?.trim() || 'Vendor not set'} · ${order.poNumber || 'PO'} · ${fmt(order.pending)} ${order.unit} pending`).join(' — ')}</Typography>
          <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>{noPoDuplicates.map((order) => <Button key={order.id} size="small" variant="contained" onClick={() => { setVendorKey(normVendor(order.vendor)); setQty({}); setActive(order.id); setPad(noPo.qty); setStep('qty'); }} sx={{ minHeight: 38, borderRadius: '15px', bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D } }}>Receive against {order.poNumber || 'this PO'}</Button>)}</Stack>
        </Box>}
        <Autocomplete<VendorOption, false, false, true>
          freeSolo
          options={vendorChoices}
          groupBy={(option) => option.group}
          getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
          filterOptions={(options, state) => {
            const byName = filterVendors(options, state);
            const typed = state.inputValue.trim();
            // The receiver may only recognize the transporter on the challan, not which vendor it delivers for.
            const byTransporter = typed ? options.filter((option) => {
              if (byName.includes(option)) return false;
              const match = cartingAgentsFor(cartingAgentsByVendor, option.name).find((agent) => agent.toLowerCase().includes(typed.toLowerCase()));
              return !!match;
            }).map((option) => ({
              ...option, group: 'Matches transporter' as const,
              hint: `via ${cartingAgentsFor(cartingAgentsByVendor, option.name).find((agent) => agent.toLowerCase().includes(typed.toLowerCase()))}`,
            })) : [];
            const filtered = [...byName, ...byTransporter];
            if (typed && ![...vendorInfo.related, ...vendorInfo.others].some((entry) => sameVendorName(entry.name, typed))) filtered.push({ name: typed, hint: '', group: 'New vendor', isNew: true });
            return filtered;
          }}
          inputValue={noPo.vendor}
          onInputChange={(_, value) => setNoPo((previous) => ({ ...previous, vendor: value }))}
          onChange={(_, value) => { if (value && typeof value !== 'string') setNoPo((previous) => ({ ...previous, vendor: value.name })); }}
          renderOption={(props, option) => { const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string }; return <li key={key} {...rest}>{option.isNew ? `Add “${option.name}” as a new vendor` : <span>{option.name}{option.hint && <Typography component="span" sx={{ ml: 1, fontSize: 12, color: option.hint === 'No rate added' ? WARN : MUTED }}>{option.hint}</Typography>}</span>}</li>; }}
          renderInput={(params) => <TextField {...params} label="Vendor" />}
        />
        {vendorQuickPicks.length > 0 && <Stack direction="row" gap={0.75} flexWrap="wrap">
          {vendorQuickPicks.map((name) => <Chip key={name} label={name} onClick={() => setNoPo((previous) => ({ ...previous, vendor: name }))}
            variant={sameVendorName(noPo.vendor, name) ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, bgcolor: sameVendorName(noPo.vendor, name) ? GOLD_WASH : 'transparent', borderColor: GOLD, color: GOLD_D }} />)}
        </Stack>}
        {noPoPacking
          ? <Stack gap={1} sx={{ p: 1.25, borderRadius: '15px', bgcolor: GOLD_WASH }}>
            <Typography sx={{ fontWeight: 700, color: GOLD_D, fontSize: 13 }}>Receive in boxes</Typography>
            <Stack direction="row" gap={0.75}>
              <TextField label="Boxes received" value={noPo.boxes} onChange={(event) => setNoPo((previous) => ({ ...previous, boxes: event.target.value, qty: String(boxesToQty(event.target.value, previous.piecesPerBox || String(noPoPacking.piecesPerBox), noPoPacking)) }))} inputProps={{ inputMode: 'decimal' }} fullWidth />
              <TextField label="Tiles / box" value={noPoPiecesPerBox} onChange={(event) => setNoPo((previous) => ({ ...previous, piecesPerBox: event.target.value, qty: String(boxesToQty(previous.boxes, event.target.value, noPoPacking)) }))} inputProps={{ inputMode: 'numeric' }} fullWidth />
            </Stack>
            <Typography sx={{ fontSize: 13, color: DARK }}>Calculated: <b>{fmt(noPoQuantity)} {noPoPick?.unit}</b> ({fixed2(noPoPacking.areaPerPiece)} {noPoPick?.unit} per tile)</Typography>
          </Stack>
          : !noPo.other && noPoPick && noPoPick.item.pieceEntry && pieceShapeForUnit(noPoPick.unit) > 0
          ? <PieceRowsEditor key={noPoPick.key} unit={noPoPick.unit} defaultDimensionUnit={noPoPick.item.pieceDimensionUnit || defaultDimensionUnitFor(noPoPick.unit)} onTotal={(total) => setNoPo((previous) => ({ ...previous, qty: total > 0 ? String(total) : '' }))} />
          : <TextField label={`Quantity received${noPo.other ? ` (${noPo.unit})` : noPoPick ? ` (${noPoPick.unit})` : ''}`} value={noPo.qty} onChange={(event) => setNoPo({ ...noPo, qty: event.target.value })} inputProps={{ inputMode: 'decimal' }} fullWidth />}
        <Button variant="outlined" onClick={addNoPoLine} sx={{ minHeight: 44, borderRadius: '15px', borderColor: BORDER_STRONG, color: GOLD_D, fontWeight: 600, textTransform: 'none' }}>+ Add material to this challan</Button>
        {deliveryDetailsFields()}
        {duplicateReceiptWarning && <Alert severity="warning" sx={{ borderRadius: '15px' }}>{duplicateReceiptWarning}</Alert>}
        {error && <Typography sx={{ color: RED, fontSize: 13 }}>{error}</Typography>}
      </Stack>
      <Footer><Primary fullWidth disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save receipt'}</Primary></Footer>
    </>;
  } else {
    content = <>
      <Bar title="Saved" />
      <Stack alignItems="center" justifyContent="center" gap={1.25} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#e5f2ea', color: GREEN, display: 'grid', placeItems: 'center', fontSize: 30 }}>✓</Box>
        <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 21 }}>Receipt saved</Typography>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Challan {reference}{photo ? ' · photo attached' : ''}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>{doneText}</Typography>
        <Button variant="outlined" onClick={reset} sx={{ mt: 1, minHeight: 48, borderRadius: '15px', color: DARK, borderColor: BORDER_STRONG }}>Receive another</Button>
      </Stack>
    </>;
  }

  // On a phone the outer card border just eats width; only a wide window (where this could sit next to Recent receipts) keeps it.
  return <Box sx={{ maxWidth: 440, mx: 'auto', width: '100%', minHeight: 560, display: 'flex', flexDirection: 'column', ...(isDesktop ? { border: `1px solid ${BORDER_STRONG}`, borderRadius: '15px' } : {}), bgcolor: '#fff', overflow: 'hidden' }}>
    {content}
    {step === 'home' && error && <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>}
  </Box>;
}
